import {Params} from '@feathersjs/feathers'

import {
  Infill,
  Option,
  OptionId,
  OptionWithLinkedProviderInfos,
  ProductionTimeOption,
  ProviderId,
  Tolerance,
} from '../../types'

import * as feathersService from '../feathers'

export type OptionRequest = Omit<Option, 'id'> | Omit<OptionWithLinkedProviderInfos, 'id'>

export const RESOURCE = 'option'

const get = async <OptionType = Option>(params?: Params) =>
  feathersService.find<OptionType>(RESOURCE, params)

export const getById = async (id: OptionId) => feathersService.get<Option>(RESOURCE, id)

export const getInfillsWithLinkedProviderInfos = async (params?: Params) => {
  const parameters = {
    ...params,
    query: {
      ...params?.query,
      type: {
        $in: Object.values(Infill),
      },
      $modify: 'includeProviderInfos',
    },
  }
  return get<OptionWithLinkedProviderInfos>(parameters)
}

export const getInfillsWithLinkedProviderInfosByProvider = async (provider: ProviderId) => {
  const params = {
    query: {
      provider,
    },
  }
  return getInfillsWithLinkedProviderInfos(params)
}

export const getInfills = async (params?: Params) => {
  const parameters = {
    ...params,
    query: {
      ...params?.query,
      type: {
        $in: Object.values(Infill),
      },
    },
  }
  return get<Option>(parameters)
}

export const getInfillsByProvider = async (provider: string) => {
  const parameters = {
    query: {
      provider,
    },
  }
  return getInfills(parameters)
}

export const getTolerancesWithLinkedProviderInfos = async (params?: Params) => {
  const parameters = {
    ...params,
    query: {
      ...params?.query,
      type: {
        $in: Object.values(Tolerance),
      },
      $modify: 'includeProviderInfos',
    },
  }
  return get<OptionWithLinkedProviderInfos>(parameters)
}

export const getTolerancesWithLinkedProviderInfosByProvider = async (provider: ProviderId) => {
  const params = {
    query: {
      provider,
    },
  }
  return getTolerancesWithLinkedProviderInfos(params)
}

export const getTolerances = async (params?: Params) => {
  const parameters = {
    ...params,
    query: {
      ...params?.query,
      type: {
        $in: Object.values(Tolerance),
      },
    },
  }
  return get<Option>(parameters)
}

export const getTolerancesByProvider = async (provider: string) => {
  const parameters = {
    query: {
      provider,
    },
  }
  return getTolerances(parameters)
}

export const getProductionTimeOptionsWithLinkedProviderInfos = async (params?: Params) => {
  const parameters = {
    ...params,
    query: {
      ...params?.query,
      type: {
        $in: Object.values(ProductionTimeOption),
      },
      $modify: 'includeProviderInfos',
    },
  }
  return get<OptionWithLinkedProviderInfos>(parameters)
}

export const getProductionTimeOptionsWithLinkedProviderInfosByProvider = async (
  provider: ProviderId
) => {
  const params = {
    query: {
      provider,
    },
  }
  return getProductionTimeOptionsWithLinkedProviderInfos(params)
}

export const getProductionTimeOptions = async (params?: Params) => {
  const parameters = {
    ...params,
    query: {
      ...params?.query,
      type: {
        $in: Object.values(ProductionTimeOption),
      },
    },
  }
  return get<Option>(parameters)
}

export const getProductionTimeOptionsByProvider = async (provider: ProviderId) => {
  const params = {
    query: {
      provider,
    },
  }
  return getProductionTimeOptions(params)
}

export const create = async (payload: OptionRequest) =>
  feathersService.create<Option, OptionRequest>(RESOURCE, payload)

export const createMultiple = async (payload: OptionRequest[]) =>
  payload.length > 0
    ? feathersService.createMultiple<Option, OptionRequest>(RESOURCE, payload)
    : Promise.resolve([])

export const update = async (payload: Option, id: OptionId) =>
  feathersService.update<Option>(RESOURCE, id, payload)

export const updateMultiple = async (payload: Option, ids: OptionId[]) =>
  Promise.all(ids.map(id => update(payload, id)))

export const patch = async (payload: Partial<OptionRequest>, id: OptionId) =>
  feathersService.patch<Option>(RESOURCE, id, payload)

export const patchMultiple = async (payload: Partial<OptionRequest>, ids: OptionId[]) =>
  Promise.all(ids.map(id => patch(payload, id)))

export const remove = async (id: OptionId) => feathersService.remove<Option>(RESOURCE, id)

export const removeMultiple = async (ids: OptionId[]) => Promise.all(ids.map(id => remove(id)))
